import { useUuiContext } from '@epam/uui-core';
import DeleteModalBotIcon from 'assets/icons/delete-modal-bot.svg';
import ConfirmBotIcon from 'assets/icons/confirm-modal-bot.svg';
import SaveModalBotIcon from 'assets/icons/not-saved-bot.svg';
import {
  MODAL_ACTION,
  MODAL_ACTION_TYPE,
  Modal,
  ModalFnArgs,
} from 'components/modals';
import { ModalWrapper } from '../../components/modals/ModalWrapper';
import { ModalWrapperFnArg } from '../../components/modals/ModalWrapper/ModalWrapper.model';

interface MethodsType {
  save: (options: ModalFnArgs) => void;
  delete: (options: ModalFnArgs) => void;
  confirm: (options: ModalFnArgs) => void;
  showdata: (options: ModalWrapperFnArg) => void;
}

export const useModal = () => {
  const { uuiModals } = useUuiContext();

  const useSaveModal = (options: ModalFnArgs) => {
    const {
      text,
      highlights,
      successAction,
      abortAction,
      isDisabled,
      onSuccess,
      onAbort = () => {},
      onClose = () => {},
    } = options;

    uuiModals
      .show(({ abort, success, isActive, key, zIndex }) => (
        <Modal
          context={{ abort, success, isActive, key, zIndex }}
          data={{
            text,
            highlights,
            title: 'Confirm your action',
            icon: SaveModalBotIcon,
            successAction: successAction ?? MODAL_ACTION.SAVE,
            abortAction: abortAction ?? MODAL_ACTION.DISCARD,
          }}
          meta={{
            onSuccess,
            onAbort,
            onClose,
            isDisabled,
            actionType: MODAL_ACTION_TYPE.SAVE,
          }}
        />
      ))
      .catch((e) => console.error(e));
  };

  const useDeleteModal = (options: ModalFnArgs) => {
    const {
      text,
      highlights,
      successAction,
      abortAction,
      onSuccess,
      onAbort = () => {},
      onClose = () => {},
    } = options;

    uuiModals
      .show(({ abort, success, isActive, key, zIndex }) => (
        <Modal
          context={{ abort, success, isActive, key, zIndex }}
          data={{
            text,
            highlights,
            title: 'Confirm your action',
            icon: DeleteModalBotIcon,
            successAction: successAction ?? MODAL_ACTION.DELETE,
            abortAction: abortAction ?? MODAL_ACTION.CANCEL,
          }}
          meta={{
            onSuccess,
            onAbort,
            onClose,
            actionType: MODAL_ACTION_TYPE.DELETE,
          }}
        />
      ))
      .catch((e) => console.error(e));
  };

  const useConfirmModal = (options: ModalFnArgs) => {
    const {
      title,
      text,
      highlights,
      successAction,
      abortAction,
      onSuccess,
      onAbort = () => {},
      onClose = () => {},
    } = options;

    uuiModals
      .show(({ abort, success, isActive, key, zIndex }) => (
        <Modal
          context={{ abort, success, isActive, key, zIndex }}
          data={{
            text,
            highlights,
            title: title ?? 'Confirm your action',
            icon: ConfirmBotIcon,
            successAction: successAction ?? MODAL_ACTION.YES,
            abortAction: abortAction ?? MODAL_ACTION.CANCEL,
          }}
          meta={{
            onSuccess,
            onAbort,
            onClose,
            actionType: MODAL_ACTION_TYPE.CONFIRM,
          }}
        />
      ))
      .catch((e) => console.error(e));
  };

  const useShowData = (options: ModalWrapperFnArg) => {
    const { data, width, height, children } = options;

    uuiModals
      .show(({ abort, isActive, key, zIndex }) => (
        <ModalWrapper
          context={{ abort, isActive, key, zIndex }}
          data={data}
          height={height}
          width={width}>
          {children}
        </ModalWrapper>
      ))
      .catch((e) => console.error(e));
  };

  const methods: MethodsType = {
    save: useSaveModal,
    delete: useDeleteModal,
    confirm: useConfirmModal,
    showdata: useShowData,
  };

  return methods;
};
